import type { AppTheme } from './ThemeContext';

export const BRAND_GREEN = '#008540';
export const BRAND_GREEN_CC = '#008540CC';
export const BRAND_GREEN_SOFT_BG = '#00854014';
export const BRAND_GOLD = '#34d399';
export const BRAND_FOREST_700 = '#008540';
export const BRAND_SAGE_MUTED = '#6b7280';
export const DASH_FONT_SERIF = "'Poppins', sans-serif";
export const DASH_FONT_MONO = "'Poppins', sans-serif";

/** Forest topbar icon (help / theme / bell) — translucent on green, amber accent in dark. */
export function forestTopbarIconButtonClass(theme: AppTheme): string {
  const base =
    'flex h-9 w-9 items-center justify-center rounded-full border-0 transition-colors duration-150';
  if (theme === 'light') {
    return `${base} bg-white/14 text-[#bcdcc7] hover:bg-[#008540] hover:text-white`;
  }
  return `${base} bg-white/[0.06] text-[#8b949e] hover:bg-[#008540] hover:text-white`;
}

export function forestTopbarClass(theme: AppTheme): string {
  return theme === 'light'
    ? 'bg-[#008540] text-[#f3efe1]'
    : 'bg-[#161b22] text-[#e6edf3]';
}

export function forestSearchInputClass(theme: AppTheme): string {
  return theme === 'light'
    ? 'w-full h-[38px] pl-10 pr-9 rounded-full bg-white/14 border-0 text-[13px] text-[#eef2e7] placeholder:text-[#bcdcc7] focus:outline-none focus:ring-0'
    : 'w-full h-[38px] pl-10 pr-9 rounded-full bg-white/[0.06] border-0 text-[13px] text-[#e6edf3] placeholder:text-[#8b949e] focus:outline-none focus:ring-0';
}

export function toolbarIconButtonClass(theme: AppTheme, size: 'sm' | 'md' = 'md'): string {
  const sizeClass = size === 'sm' ? 'h-9 w-9' : 'h-10 w-10';
  const iconClass = size === 'sm' ? 'text-lg' : 'text-xl';

  if (theme === 'light') {
    return [
      'flex items-center justify-center rounded-full backdrop-blur-sm shadow-md transition-colors',
      sizeClass,
      'bg-white/95 border border-gray-200 text-[#008540CC] hover:bg-[#00854014] hover:border-[#00854080]',
    ].join(' ');
  }

  return [
    'flex items-center justify-center rounded-full backdrop-blur-sm shadow-md transition-colors',
    sizeClass,
    'bg-gray-800/95 border border-gray-600 text-emerald-400 hover:bg-gray-700 hover:border-gray-500',
  ].join(' ');
}

export function toolbarBellButtonClass(theme: AppTheme, size: 'sm' | 'md' = 'md'): string {
  const base = toolbarIconButtonClass(theme, size);
  if (theme === 'light') {
    return `${base} !text-amber-500 hover:!bg-amber-50 hover:!border-amber-300`;
  }
  return `${base} !text-amber-400 hover:!bg-amber-950/40 hover:!border-amber-700/60`;
}

export function toolbarHelpButtonClass(theme: AppTheme, size: 'sm' | 'md' = 'md'): string {
  const base = toolbarIconButtonClass(theme, size);
  if (theme === 'light') {
    return `${base} hover:!text-[#008540]`;
  }
  return base;
}

export function pageShellClass(theme: AppTheme): string {
  return theme === 'dark' ? 'bg-[#0d1117]' : 'bg-[#f8faf7]';
}

/** Onboarding tour tooltip + prompt modal. */
export function getTourTheme(isDark: boolean) {
  return {
    tooltip: isDark
      ? 'bg-[#161b22] border border-[#2d3748] rounded-[20px] shadow-[0_1px_2px_rgba(0,0,0,.4),0_10px_26px_-14px_rgba(0,0,0,.7)] p-5'
      : 'bg-white border border-[#e5e7eb] rounded-[20px] shadow-[0_1px_2px_rgba(20,25,20,.04),0_10px_24px_-14px_rgba(20,25,20,.18)] p-5',
    tooltipFallback: isDark
      ? 'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-[#161b22] border border-[#2d3748] rounded-[20px] shadow-[0_1px_2px_rgba(0,0,0,.4),0_10px_26px_-14px_rgba(0,0,0,.7)] p-5'
      : 'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white border border-[#e5e7eb] rounded-[20px] shadow-[0_1px_2px_rgba(20,25,20,.04),0_10px_24px_-14px_rgba(20,25,20,.18)] p-5',
    stepLabel: isDark
      ? 'text-[11px] font-semibold uppercase tracking-wider text-[#34d399] font-mono'
      : 'text-[11px] font-semibold uppercase tracking-wider text-[#008540] font-mono',
    title: isDark
      ? 'text-base font-semibold text-[#e6edf3] mb-1.5'
      : 'text-base font-semibold text-[#111827] mb-1.5',
    body: isDark
      ? 'text-sm text-[#8b949e] leading-relaxed'
      : 'text-sm text-[#6b7280] leading-relaxed',
    note: isDark
      ? 'mt-2 text-xs text-[#34d399] italic'
      : 'mt-2 text-xs text-[#008540] italic',
    skipBtn: isDark
      ? 'text-xs text-[#8b949e] hover:text-[#c9d1d9] underline-offset-2 hover:underline'
      : 'text-xs text-[#6b7280] hover:text-[#008540] underline-offset-2 hover:underline',
    closeBtn: isDark
      ? 'text-[#8b949e] hover:text-[#e6edf3] hover:bg-white/[0.06] p-1 -mr-1 rounded-[8px]'
      : 'text-[#6b7280] hover:text-[#008540] hover:bg-[#f3f4f6] p-1 -mr-1 rounded-[8px]',
    prevBtn: isDark
      ? 'px-3 py-1.5 rounded-[10px] border border-[#2d3748] text-sm text-[#c9d1d9] bg-[#1c2432] hover:bg-[#222]'
      : 'px-3 py-1.5 rounded-[10px] border border-[#e5e7eb] text-sm text-[#374151] bg-white hover:bg-[#f3f4f6]',
    nextBtn: isDark
      ? 'px-3 py-1.5 rounded-[10px] bg-[#008540] text-white text-sm font-semibold hover:bg-[#097a34]'
      : 'px-3 py-1.5 rounded-[10px] bg-[#008540] text-white text-sm font-semibold hover:opacity-90',
    ring: isDark
      ? 'absolute rounded-[14px] ring-2 ring-[#34d399] shadow-[0_0_0_3px_rgba(52,211,153,0.28)] pointer-events-none transition-all duration-200'
      : 'absolute rounded-[14px] ring-2 ring-[#008540] shadow-[0_0_0_3px_rgba(0,133,64,0.2)] pointer-events-none transition-all duration-200',
    promptOverlay: isDark ? 'absolute inset-0 bg-black/70' : 'absolute inset-0 bg-black/40',
    promptPanel: isDark
      ? 'rounded-[20px] bg-[#161b22] shadow-[0_1px_2px_rgba(0,0,0,.4),0_10px_26px_-14px_rgba(0,0,0,.7)] border border-[#2d3748] p-6 sm:p-7'
      : 'rounded-[20px] bg-white shadow-[0_1px_2px_rgba(20,25,20,.04),0_10px_24px_-14px_rgba(20,25,20,.18)] border border-[#e5e7eb] p-6 sm:p-7',
    promptIconWrap: isDark
      ? 'shrink-0 flex items-center justify-center w-11 h-11 rounded-[10px] bg-[rgba(52,211,153,.16)] text-[#34d399]'
      : 'shrink-0 flex items-center justify-center w-11 h-11 rounded-[10px] bg-[#00854014] text-[#008540]',
    promptTitle: isDark
      ? 'text-lg font-semibold text-[#e6edf3] leading-tight'
      : 'text-lg font-semibold text-[#111827] leading-tight',
    promptSub: isDark ? 'text-sm text-[#8b949e] mt-0.5' : 'text-sm text-[#6b7280] mt-0.5',
    promptBody: isDark
      ? 'text-sm text-[#c9d1d9] leading-relaxed'
      : 'text-sm text-[#374151] leading-relaxed',
    promptPrimary: isDark
      ? 'w-full py-2.5 rounded-[10px] bg-[#008540] text-white font-semibold text-sm hover:bg-[#097a34] transition-colors inline-flex items-center justify-center gap-2'
      : 'w-full py-2.5 rounded-[10px] bg-[#008540] text-white font-semibold text-sm hover:opacity-90 transition-colors inline-flex items-center justify-center gap-2',
    promptSecondary: isDark
      ? 'flex-1 py-2 rounded-[10px] border border-[#2d3748] text-sm text-[#c9d1d9] bg-[#1c2432] hover:bg-[#222] transition-colors'
      : 'flex-1 py-2 rounded-[10px] border border-[#e5e7eb] text-sm text-[#374151] bg-white hover:bg-[#f3f4f6] transition-colors',
    promptTertiary: isDark
      ? 'flex-1 py-2 rounded-[10px] text-sm text-[#8b949e] hover:text-[#c9d1d9] hover:bg-white/[0.06] transition-colors'
      : 'flex-1 py-2 rounded-[10px] text-sm text-[#6b7280] hover:text-[#008540] hover:bg-[#f3f4f6] transition-colors',
    promptFootnote: isDark
      ? 'mt-4 text-[11px] text-[#8b949e] text-center'
      : 'mt-4 text-[11px] text-[#6b7280] text-center',
  };
}

export function mobileHeaderClass(theme: AppTheme): string {
  return forestTopbarClass(theme);
}

export function sidebarShellClass(theme: AppTheme, isOpen: boolean): string {
  const base =
    theme === 'dark'
      ? 'bg-gray-900 text-white border-gray-700'
      : 'bg-white text-gray-900 border-gray-200';
  return `fixed left-0 top-0 h-full w-80 transform transition-transform duration-300 z-50 border-r ${base} ${
    isOpen ? 'translate-x-0' : '-translate-x-full'
  } lg:translate-x-0 lg:static lg:z-auto`;
}

export function analysisPanelShellClass(theme: AppTheme, isInlineCard: boolean, isOpen: boolean): string {
    if (isInlineCard) {
    return theme === 'dark'
      ? 'analysis-panel-compact analysis-panel-shell analysis-panel-shell--dark bg-[rgba(22,27,34,.95)] text-[#e6edf3] rounded-[20px] border-0 h-full w-full flex flex-col overflow-hidden'
      : 'analysis-panel-compact analysis-panel-shell analysis-panel-shell--light bg-[rgba(248,245,236,.96)] text-[#111827] rounded-[20px] border-0 h-full w-full flex flex-col overflow-hidden';
  }

  const drawerMotion = `fixed right-0 top-0 h-full w-full sm:w-96 transform transition-transform duration-300 z-50 ${
    isOpen ? 'translate-x-0' : 'translate-x-full'
  } lg:relative lg:translate-x-0 lg:block overflow-y-auto`;

  return theme === 'dark'
    ? `${drawerMotion} bg-gray-900 text-white`
    : `${drawerMotion} bg-white text-gray-900 border-l border-gray-200`;
}

/** Desktop map tool rail — rounded card tiles; colors match pre-mock dark/light theme. */
export function getMapRailTheme(isDark: boolean) {
  return {
    shell:
      'hidden sm:grid grid-cols-[5.625rem_5.625rem] place-items-center justify-items-center map-tool-rail-shell p-2.5 gap-2 rounded-[20px] w-fit',
    shellDark: 'map-tool-rail-shell--dark bg-[rgba(22,27,34,.95)] border-0 shadow-[0_1px_2px_rgba(0,0,0,.4),0_10px_26px_-14px_rgba(0,0,0,.7)]',
    shellLight: 'map-tool-rail-shell--light bg-[rgba(248,245,236,.96)] border-0 shadow-[0_1px_2px_rgba(20,25,20,.04),0_10px_24px_-14px_rgba(20,25,20,.18)]',
    /** Fixed card rectangle (5.625rem × 5.25rem) — icon + label centered on X and Y. */
    toolCard:
      'group relative box-border flex h-[5.25rem] w-[5.625rem] max-w-[5.625rem] shrink-0 items-center justify-center rounded-[14px] border text-center transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#008540CC] focus-visible:ring-offset-0',
    toolCardInner: 'flex flex-col items-center justify-center gap-0.5 px-1',
    cellHoverDark: 'hover:border-white/12',
    cellHoverLight: 'hover:border-[#d1d5db]',
    cellIdleDark: 'border-[#2d3748] bg-[#161b22]',
    cellIdleLight: 'border-[#e5e7eb] bg-white',
    cellFarmIdleDark: 'border-[#2d3748] bg-[#161b22]',
    cellFarmIdleLight: 'border-[#e5e7eb] bg-white',
    cellActiveDark: 'border-transparent bg-[#008540]',
    cellActiveLight: 'border-transparent bg-[#008540]',
    label: 'font-medium text-center [overflow-wrap:anywhere]',
    labelCompact: 'max-w-full text-[11px] font-semibold leading-[1.25]',
    labelDefault: 'text-[11px] leading-tight',
    labelActiveDark: '#ffffff',
    labelActiveLight: '#ffffff',
    labelFarmDark: '#c9d1d9',
    labelFarmLight: '#374151',
    labelMutedDark: '#c9d1d9',
    labelMutedLight: '#374151',
    iconCircleCompact: 'h-8 w-8 shrink-0',
    iconCircleDefault: 'h-11 w-11 shrink-0',
    hideButtonDark:
      'border-gray-600 bg-gray-800 text-gray-300 shadow-md hover:bg-gray-700 hover:text-gray-100',
    hideButtonLight:
      'border-gray-200 bg-white text-gray-600 shadow-md hover:bg-gray-50 hover:text-gray-900',
    hideButtonOnShellDark:
      'border-0 bg-transparent text-[#8b949e] hover:text-[#34d399]',
    hideButtonOnShellLight:
      'border-gray-200 bg-white text-gray-600 shadow-md hover:bg-gray-50 hover:text-gray-900',
    sheetIconButtonDark: 'text-gray-400 hover:bg-gray-800 hover:text-gray-200',
    sheetIconButtonLight: 'text-gray-500 hover:bg-gray-100 hover:text-gray-700',
    sheetTitleDark: 'text-white',
    sheetTitleLight: 'text-gray-900',
    sheetHandleDark: 'bg-gray-600',
    sheetHandleLight: 'bg-gray-300',
    launcherNotifyRingDark: 'ring-gray-900',
    launcherNotifyRingLight: 'ring-white',
    farmDotRingIdleDark: 'ring-gray-800',
    farmDotRingIdleLight: 'ring-white',
    farmDotRingActiveDark: 'ring-[#00854018]',
    farmDotRingActiveLight: 'ring-[#00854014]',
    mobileSheetDark: 'bg-gray-900/90 border-t border-gray-700',
    mobileSheetLight: 'bg-white/90 border-t border-gray-200',
    mobileGrid: 'grid grid-cols-2 place-items-center justify-items-center gap-2.5 px-4 pb-4 pt-1',
  };
}

export function mapRailShellClass(theme: AppTheme): string {
  const rail = getMapRailTheme(theme === 'dark');
  return `${rail.shell} ${theme === 'dark' ? rail.shellDark : rail.shellLight}`;
}

export function mapRailCellHoverClass(theme: AppTheme): string {
  const rail = getMapRailTheme(theme === 'dark');
  return theme === 'dark' ? rail.cellHoverDark : rail.cellHoverLight;
}

/** Visible rectangle fill/border for each tool card (idle / active / farm). */
export function mapRailToolCardClass(
  theme: AppTheme,
  isActive: boolean,
  isFarm = false,
): string {
  const rail = getMapRailTheme(theme === 'dark');
  if (isActive) {
    return theme === 'dark' ? rail.cellActiveDark : rail.cellActiveLight;
  }
  if (isFarm) {
    return theme === 'dark' ? rail.cellFarmIdleDark : rail.cellFarmIdleLight;
  }
  return theme === 'dark' ? rail.cellIdleDark : rail.cellIdleLight;
}

/** @deprecated Use `mapRailToolCardClass` — kept for any external imports. */
export function mapRailCellActiveClass(theme: AppTheme, isActive: boolean): string {
  return mapRailToolCardClass(theme, isActive);
}

export function mapRailLabelColor(theme: AppTheme, isActive: boolean, isFarm: boolean): string {
  const rail = getMapRailTheme(theme === 'dark');
  if (isActive) return theme === 'dark' ? rail.labelActiveDark : rail.labelActiveLight;
  if (isFarm) return theme === 'dark' ? rail.labelFarmDark : rail.labelFarmLight;
  return theme === 'dark' ? rail.labelMutedDark : rail.labelMutedLight;
}

/** Hide analysis tools control — `overlay` matches the rail shell surface. */
export function mapRailHideButtonClass(
  theme: AppTheme,
  opts?: { overlay?: boolean; compact?: boolean },
): string {
  const rail = getMapRailTheme(theme === 'dark');
  const surface = opts?.overlay
    ? theme === 'dark'
      ? rail.hideButtonOnShellDark
      : rail.hideButtonOnShellLight
    : theme === 'dark'
      ? rail.hideButtonDark
      : rail.hideButtonLight;
  const size = opts?.compact ? 'h-6 w-6 text-xs' : 'h-7 w-7 text-sm';
  return [
    'flex items-center justify-center rounded-full border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#008540CC]',
    size,
    surface,
  ].join(' ');
}

export function mapRailSheetIconButtonClass(theme: AppTheme): string {
  const rail = getMapRailTheme(theme === 'dark');
  return `p-1.5 rounded-full transition-colors ${theme === 'dark' ? rail.sheetIconButtonDark : rail.sheetIconButtonLight}`;
}

export function mapRailLauncherNotifyRingClass(theme: AppTheme): string {
  const rail = getMapRailTheme(theme === 'dark');
  return theme === 'dark' ? rail.launcherNotifyRingDark : rail.launcherNotifyRingLight;
}

export function mapRailFarmDotRingClass(theme: AppTheme, isActive: boolean): string {
  const rail = getMapRailTheme(theme === 'dark');
  if (isActive) {
    return theme === 'dark' ? rail.farmDotRingActiveDark : rail.farmDotRingActiveLight;
  }
  return theme === 'dark' ? rail.farmDotRingIdleDark : rail.farmDotRingIdleLight;
}

/** Map-bottom time series player (field + WMS layers). */
export function getTimeSeriesSliderTheme(isDark: boolean) {
  const fill = isDark ? '#34d399' : BRAND_GREEN;
  const track = isDark ? '#253347' : '#e5e7eb';
  return {
    shell: isDark
      ? 'time-series-slider-shell rounded-[14px] border-0 bg-[rgba(22,27,34,.95)] p-3 shadow-[0_1px_2px_rgba(0,0,0,.4),0_10px_26px_-14px_rgba(0,0,0,.7)] backdrop-blur-[6px]'
      : 'time-series-slider-shell rounded-[14px] border-0 bg-[rgba(248,245,236,.96)] p-3 shadow-[0_1px_2px_rgba(20,25,20,.04),0_10px_24px_-14px_rgba(20,25,20,.18)] backdrop-blur-[6px]',
    shellCompact: isDark
      ? 'time-series-slider-shell rounded-[14px] border-0 bg-[rgba(22,27,34,.95)] p-2 shadow-[0_1px_2px_rgba(0,0,0,.4),0_10px_26px_-14px_rgba(0,0,0,.7)] backdrop-blur-[6px]'
      : 'time-series-slider-shell rounded-[14px] border-0 bg-[rgba(248,245,236,.96)] p-2 shadow-[0_1px_2px_rgba(20,25,20,.04),0_10px_24px_-14px_rgba(20,25,20,.18)] backdrop-blur-[6px]',
    title: isDark ? 'text-sm font-semibold text-[#e6edf3]' : 'text-sm font-semibold text-[#111827]',
    titleIcon: isDark ? 'text-[#34d399]' : 'text-[#008540]',
    subtitle: isDark ? 'text-lg font-semibold text-[#e6edf3]' : 'text-lg font-semibold text-[#111827]',
    badge: isDark
      ? 'text-xs text-[#c9d1d9] bg-[#1c2432] px-2 py-1 rounded-full'
      : 'text-xs text-[#6b7280] bg-[#f3f4f6] px-2 py-1 rounded-full',
    counter: isDark ? 'text-xs text-gray-400' : 'text-xs text-gray-500',
    compareBadge: isDark
      ? 'ml-2 px-1.5 py-0.5 bg-gray-800 text-gray-400 rounded text-[10px] font-medium'
      : 'ml-2 px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded text-[10px] font-medium',
    compareActive: isDark
      ? 'border border-gray-600 text-gray-200 bg-gray-800/80 hover:bg-gray-700'
      : 'border border-gray-300 text-gray-700 bg-gray-50 hover:bg-gray-100',
    compareIdle: isDark
      ? 'bg-gray-800 text-gray-200 hover:bg-gray-700'
      : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
    compareDisabled: 'bg-gray-200 text-gray-400 cursor-not-allowed opacity-60',
    playActive: 'bg-red-600 text-white hover:bg-red-700',
    playIdle: isDark
      ? 'bg-[#008540] text-white hover:bg-[#097a34]'
      : 'bg-[#008540] text-white hover:opacity-90',
    navBtn: isDark
      ? 'bg-[#1c2432] hover:bg-[#222] disabled:opacity-50 text-[#c9d1d9]'
      : 'bg-[#f3f4f6] hover:bg-[#e5e7eb] disabled:opacity-50 text-[#374151]',
    playCircle: isDark
      ? 'bg-[#008540] hover:bg-[#097a34] text-white'
      : 'bg-[#008540] hover:opacity-90 text-white',
    speedSelect: isDark
      ? 'bg-gray-800 border border-gray-600 rounded px-2 py-1 text-xs text-gray-200 focus:outline-none focus:ring-2 focus:ring-emerald-500'
      : 'bg-white border border-gray-300 rounded px-2 py-1 text-xs text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#008540]',
    speedLabel: isDark ? 'text-xs text-gray-400' : 'text-xs text-gray-600',
    stepText: isDark ? 'text-[10px] text-gray-500 mb-1.5' : 'text-[10px] text-gray-500 mb-1.5',
    stepEm: isDark ? 'font-medium text-gray-400' : 'font-medium text-gray-600',
    beforeLabel: isDark ? 'text-[10px] text-gray-400 font-medium' : 'text-[10px] text-gray-600 font-medium',
    afterLabel: isDark ? 'text-[10px] text-gray-400 font-medium' : 'text-[10px] text-gray-600 font-medium',
    afterLabelDisabled: 'text-[10px] text-gray-500 font-medium',
    beforeSelect: isDark
      ? 'w-full text-[11px] py-1 px-2 border border-gray-600 border-l-2 border-l-blue-400/70 rounded bg-gray-800 text-gray-100 focus:outline-none focus:ring-1 focus:ring-gray-500'
      : 'w-full text-[11px] py-1 px-2 border border-gray-200 border-l-2 border-l-blue-500/60 rounded bg-white text-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-300',
    afterSelect: isDark
      ? 'w-full text-[11px] py-1 px-2 border border-gray-600 border-l-2 border-l-orange-400/70 rounded bg-gray-800 text-gray-100 focus:outline-none focus:ring-1 focus:ring-gray-500'
      : 'w-full text-[11px] py-1 px-2 border border-gray-200 border-l-2 border-l-orange-500/60 rounded bg-white text-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-300',
    afterSelectDisabled: isDark
      ? 'border-gray-700 bg-gray-800 text-gray-500 cursor-not-allowed'
      : 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed',
    hint: isDark ? 'text-[10px] text-amber-400 mt-1' : 'text-[10px] text-amber-700 mt-1',
    dateEdge: isDark ? 'text-xs text-gray-400' : 'text-xs text-gray-500',
    statBox: isDark ? 'text-center bg-[#1c2432] rounded-[10px] p-2' : 'text-center bg-[#f3f4f6] rounded-[10px] p-2 border border-[#e5e7eb]',
    statBoxIndex: isDark ? 'text-center bg-[rgba(52,211,153,.12)] rounded-[10px] p-2' : 'text-center bg-[#00854014] rounded-[10px] p-2',
    statBoxProgress: isDark ? 'text-center bg-blue-950/50 rounded p-2' : 'text-center bg-blue-50 rounded p-2',
    statBoxCloud: isDark ? 'text-center bg-purple-950/50 rounded p-2' : 'text-center bg-purple-50 rounded p-2',
    statLabel: isDark ? 'text-gray-400 text-xs mb-1' : 'text-gray-600 text-xs mb-1',
    statValue: isDark ? 'font-semibold text-gray-100' : 'font-semibold text-gray-900',
    statValueIndex: isDark ? 'font-semibold text-[#34d399] text-lg' : 'font-semibold text-[#008540] text-lg',
    statValueProgress: isDark ? 'font-semibold text-blue-400' : 'font-semibold text-blue-600',
    statValueCloud: isDark ? 'font-semibold text-purple-400' : 'font-semibold text-purple-600',
    dot: isDark ? 'w-3 h-3 rounded-full bg-[#008540]' : 'w-3 h-3 rounded-full bg-[#008540]',
    progressTrack: isDark ? 'w-full bg-[#253347] rounded-full h-1' : 'w-full bg-[#e5e7eb] rounded-full h-1',
    progressBar: isDark ? 'bg-[#008540] h-1 rounded-full transition-all duration-300' : 'bg-[#008540] h-1 rounded-full transition-all duration-300',
    rangeClass: isDark
      ? 'w-full h-3 rounded-lg appearance-none cursor-pointer time-series-range time-series-range--dark'
      : 'w-full h-2 rounded-lg appearance-none cursor-pointer time-series-range slider',
    rangeFill: fill,
    rangeTrack: track,
    marker: isDark ? 'absolute w-0.5 bg-gray-500' : 'absolute w-0.5 bg-gray-400',
    bsriLabel: isDark ? 'text-sm text-gray-400' : 'text-sm text-gray-600',
    bsriValue: isDark ? 'text-base font-bold text-yellow-400' : 'text-base font-bold text-amber-600',
    bsriWeek: isDark ? 'text-xs text-gray-500' : 'text-xs text-gray-500',
  };
}

/** Zoom / layers / locate / fullscreen strip (top-left) and overview / info buttons (bottom-left). */
export function getMapControlsTheme(isDark: boolean) {
  return {
    shell: isDark
      ? 'rounded-[14px] overflow-hidden bg-[rgba(22,27,34,.95)] shadow-[0_1px_2px_rgba(0,0,0,.4),0_10px_26px_-14px_rgba(0,0,0,.7)] border-0 backdrop-blur-[6px]'
      : 'rounded-[14px] overflow-hidden bg-[rgba(248,245,236,.96)] shadow-[0_1px_2px_rgba(20,25,20,.04),0_10px_24px_-14px_rgba(20,25,20,.18)] border-0 backdrop-blur-[6px]',
    btn: isDark
      ? 'block w-10 h-10 flex items-center justify-center hover:bg-white/[0.04] transition-colors'
      : 'block w-10 h-10 flex items-center justify-center hover:bg-[#f3f4f6] transition-colors',
    btnBorder: isDark ? 'border-b border-[#2d3748]' : 'border-b border-[#e5e7eb]',
    icon: isDark ? 'text-[#c9d1d9]' : 'text-[#374151]',
    menuShell: isDark
      ? 'absolute left-12 top-0 z-50 min-w-[13.5rem] rounded-[14px] border border-[#2d3748] bg-[#161b22] p-2 shadow-[0_1px_2px_rgba(0,0,0,.4),0_10px_26px_-14px_rgba(0,0,0,.7)]'
      : 'absolute left-12 top-0 z-50 min-w-[13.5rem] rounded-[14px] border border-[#e5e7eb] bg-white p-2 shadow-[0_1px_2px_rgba(20,25,20,.04),0_10px_24px_-14px_rgba(20,25,20,.18)]',
    menuItem: isDark
      ? 'block w-full text-left px-3 py-2 text-sm rounded-[8px] transition-colors text-[#c9d1d9] hover:bg-white/[0.06]'
      : 'block w-full text-left px-3 py-2 text-sm rounded-[8px] transition-colors text-[#374151] hover:bg-[#f3f4f6]',
    menuItemActive: isDark ? 'bg-[#008540] text-white' : 'bg-[#008540] text-white',
    floatingBtn: isDark
      ? 'w-9 h-9 flex items-center justify-center rounded-[10px] shadow-[0_1px_2px_rgba(0,0,0,.4),0_10px_26px_-14px_rgba(0,0,0,.7)] border-0 bg-[rgba(22,27,34,.95)] text-[#c9d1d9] transition-colors duration-150 hover:text-[#34d399]'
      : 'w-9 h-9 flex items-center justify-center rounded-[10px] shadow-[0_1px_2px_rgba(20,25,20,.04),0_10px_24px_-14px_rgba(20,25,20,.18)] border-0 bg-[rgba(248,245,236,.96)] text-[#374151] transition-colors duration-150 hover:text-[#008540]',
    floatingBtnActive: isDark
      ? 'w-9 h-9 flex items-center justify-center rounded-[10px] shadow-[0_1px_2px_rgba(0,0,0,.4)] border-0 bg-[#008540] text-white transition-colors duration-150'
      : 'w-9 h-9 flex items-center justify-center rounded-[10px] shadow-[0_1px_2px_rgba(20,25,20,.04)] border-0 bg-[#008540] text-white transition-colors duration-150',
    infoBar: isDark
      ? 'absolute bottom-4 left-14 bg-[rgba(22,27,34,.95)] text-[#c9d1d9] px-4 py-2 rounded-[14px] text-sm z-10 flex items-center space-x-4 shadow-[0_1px_2px_rgba(0,0,0,.4)] border-0 backdrop-blur-[6px]'
      : 'absolute bottom-4 left-14 bg-[rgba(248,245,236,.96)] text-[#374151] px-4 py-2 rounded-[14px] text-sm z-10 flex items-center space-x-4 shadow-[0_1px_2px_rgba(20,25,20,.04),0_10px_24px_-14px_rgba(20,25,20,.18)] border-0 backdrop-blur-[6px]',
    infoIcon: isDark ? 'text-gray-400' : 'text-gray-600',
    loadingShell: isDark
      ? 'absolute top-4 left-1/2 transform -translate-x-1/2 z-10 rounded-[14px] border-0 bg-[rgba(22,27,34,.95)] p-3 shadow-[0_1px_2px_rgba(0,0,0,.4)] backdrop-blur-[6px]'
      : 'absolute top-4 left-1/2 transform -translate-x-1/2 z-10 rounded-[14px] border-0 bg-[rgba(248,245,236,.96)] p-3 shadow-[0_1px_2px_rgba(20,25,20,.04),0_10px_24px_-14px_rgba(20,25,20,.18)] backdrop-blur-[6px]',
    loadingText: isDark ? 'text-sm text-[#8b949e]' : 'text-sm text-[#6b7280]',
  };
}

/** Left-edge drawer tab to reopen the fields sidebar when collapsed (desktop). */
export function showFieldsPanelTabClass(theme: AppTheme): string {
  const base =
    'hidden lg:flex absolute top-1/2 left-0 -translate-y-1/2 z-30 items-center justify-center w-6 h-16 rounded-r-lg backdrop-blur-sm border border-l-0 shadow-md transition-colors';
  if (theme === 'light') {
    return `${base} bg-white/95 border-gray-200 text-[#008540] hover:bg-[#00854014] hover:border-[#00854080]`;
  }
  return `${base} bg-gray-900/95 border-gray-700 text-emerald-400 hover:bg-gray-800 hover:border-[#008540CC]`;
}

/** Nominatim search results dropdown (map overlay search). */
export function getSearchBarTheme(isDark: boolean) {
  return {
    input: isDark
      ? 'w-full pl-10 pr-9 py-2.5 rounded-full bg-[rgba(22,27,34,.95)] backdrop-blur-[6px] border border-[#2d3748] shadow-[0_1px_2px_rgba(0,0,0,.4)] text-sm text-[#e6edf3] placeholder:text-[#8b949e] focus:outline-none focus:ring-2 focus:ring-[#34d399]/40 focus:border-[#34d399]'
      : 'w-full pl-10 pr-9 py-2.5 rounded-full bg-[rgba(248,245,236,.96)] backdrop-blur-[6px] border border-[#e5e7eb] shadow-[0_1px_2px_rgba(20,25,20,.04),0_10px_24px_-14px_rgba(20,25,20,.18)] text-sm text-[#111827] placeholder:text-[#6b7280] focus:outline-none focus:ring-2 focus:ring-[#008540]/30 focus:border-[#008540]',
    icon: isDark ? 'text-[#8b949e]' : 'text-[#6b7280]',
    clearBtn: isDark
      ? 'text-[#8b949e] hover:text-[#e6edf3] p-1 rounded-full hover:bg-white/[0.06]'
      : 'text-[#6b7280] hover:text-[#008540] p-1 rounded-full hover:bg-[#f3f4f6]',
    spinner: isDark ? 'border-[#34d399]' : 'border-[#008540]',
    resultsPanel: isDark
      ? 'absolute top-full left-0 right-0 mt-1.5 bg-[#161b22] rounded-[14px] shadow-[0_1px_2px_rgba(0,0,0,.4),0_10px_26px_-14px_rgba(0,0,0,.7)] border border-[#2d3748] max-h-72 overflow-y-auto z-50'
      : 'absolute top-full left-0 right-0 mt-1.5 bg-white rounded-[14px] shadow-[0_1px_2px_rgba(20,25,20,.04),0_10px_24px_-14px_rgba(20,25,20,.18)] border border-[#e5e7eb] max-h-72 overflow-y-auto z-50',
    resultBtn: isDark
      ? 'w-full text-left px-4 py-3 hover:bg-white/[0.04] border-b border-[#2d3748] last:border-b-0 transition-colors'
      : 'w-full text-left px-4 py-3 hover:bg-[#f3f4f6] border-b border-[#e5e7eb] last:border-b-0 transition-colors',
    resultTitle: isDark
      ? 'text-sm text-[#e6edf3] font-medium truncate'
      : 'text-sm text-[#111827] font-medium truncate',
    resultSub: isDark ? 'text-xs text-[#8b949e] truncate' : 'text-xs text-[#6b7280] truncate',
    resultExact: isDark
      ? 'text-xs text-[#34d399] mt-1 font-semibold'
      : 'text-xs text-[#008540] mt-1 font-semibold',
    exactBadge: isDark
      ? 'shrink-0 px-2 py-1 bg-[rgba(52,211,153,.16)] text-[#34d399] text-xs rounded-[8px] font-semibold border border-[#34d399]/40'
      : 'shrink-0 px-2 py-1 bg-[#00854014] text-[#008540] text-xs rounded-[8px] font-semibold border border-[#00854080]',
  };
}

export function userMenuTriggerClass(theme: AppTheme, open: boolean): string {
  if (theme === 'light') {
    return `flex items-center gap-0.5 rounded-full border border-gray-200 bg-white pl-1 pr-1.5 py-1 shadow-sm transition-colors ${
      open ? 'bg-gray-50 ring-2 ring-[#00854033]' : 'hover:bg-gray-50'
    }`;
  }

  return `flex items-center gap-0.5 rounded-full border border-gray-600 bg-gray-800 pl-1 pr-1.5 py-1 shadow-sm transition-colors ${
    open ? 'bg-gray-700 ring-2 ring-emerald-500/25' : 'hover:bg-gray-700'
  }`;
}

export function userMenuDropdownClass(theme: AppTheme): string {
  return theme === 'dark'
    ? 'absolute right-0 top-full z-[110] mt-2 w-[min(calc(100vw-2rem),16rem)] rounded-xl border border-gray-700/90 bg-gray-900 p-2.5 shadow-2xl ring-1 ring-black/20'
    : 'absolute right-0 top-full z-[110] mt-2 w-[min(calc(100vw-2rem),16rem)] rounded-xl border border-gray-200 bg-white p-2.5 shadow-xl ring-1 ring-black/5';
}

export function getUserMenuTheme(isDark: boolean) {
  return {
    headerCard: isDark
      ? 'rounded-lg border border-gray-700/80 bg-gray-800/70 px-2.5 py-2'
      : 'rounded-lg border border-gray-200 bg-[#00854014] px-2.5 py-2',
    headerLabel: isDark ? 'text-[10px] uppercase tracking-wide text-gray-500' : 'text-[10px] uppercase tracking-wide text-[#008540]/80',
    headerName: isDark ? 'text-sm font-semibold text-white' : 'text-sm font-semibold text-gray-900',
    avatarWrap: 'flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-white',
    primaryBtn: isDark
      ? 'flex w-full items-center justify-center gap-1.5 rounded-lg bg-emerald-600 py-2 text-xs font-medium text-white transition-colors hover:bg-emerald-500'
      : 'flex w-full items-center justify-center gap-1.5 rounded-lg bg-[#008540] py-2 text-xs font-medium text-white transition-colors hover:opacity-90',
    secondaryBtn: isDark
      ? 'w-full rounded-lg border border-gray-600/80 bg-gray-800/50 py-2 text-xs font-medium text-gray-200 transition-colors hover:bg-gray-700'
      : 'w-full rounded-lg border border-gray-200 bg-white py-2 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50',
    adminBtn:
      'flex w-full items-center justify-center gap-1.5 rounded-lg border py-2 text-xs font-medium transition-colors ' +
      (isDark
        ? 'border-emerald-600/45 bg-emerald-900/40 text-emerald-100 hover:bg-emerald-800/50'
        : 'border-[#00854080] bg-[#00854014] text-[#008540] hover:bg-[#00854020]'),
    trialText: isDark ? 'text-[10px] leading-snug text-amber-200/90' : 'text-[10px] leading-snug text-amber-800',
    trialStrong: isDark ? 'font-semibold text-amber-100' : 'font-semibold text-amber-900',
    upgradeBanner: isDark
      ? 'space-y-1.5 rounded-lg border border-amber-500/45 bg-amber-950/35 px-2.5 py-2'
      : 'space-y-1.5 rounded-lg border border-amber-300/80 bg-amber-50 px-2.5 py-2',
    upgradeText: isDark ? 'text-[11px] font-medium leading-snug text-amber-100/95' : 'text-[11px] font-medium leading-snug text-amber-950',
    upgradeBtn: isDark
      ? 'w-full rounded-lg bg-amber-500 py-1.5 text-xs font-semibold text-gray-950 hover:bg-amber-400 transition-colors'
      : 'w-full rounded-lg bg-amber-500 py-1.5 text-xs font-semibold text-white hover:bg-amber-600 transition-colors',
  };
}

/** Confirm / alert dialog shell (delete field, etc.). */
export function getConfirmDialogTheme(isDark: boolean) {
  return {
    overlay: isDark
      ? 'fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/70'
      : 'fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/40',
    panel: isDark
      ? 'w-full max-w-sm rounded-[20px] border border-[#2d3748] bg-[#161b22] p-5 text-[#e6edf3] shadow-[0_1px_2px_rgba(0,0,0,.4),0_10px_26px_-14px_rgba(0,0,0,.7)]'
      : 'w-full max-w-sm rounded-[20px] border border-[#e5e7eb] bg-white p-5 text-[#111827] shadow-[0_1px_2px_rgba(20,25,20,.04),0_10px_24px_-14px_rgba(20,25,20,.18)]',
    title: isDark
      ? 'text-[17px] font-semibold text-[#e6edf3] mb-2'
      : 'text-[17px] font-semibold text-[#111827] mb-2',
    body: isDark
      ? 'text-sm text-[#8b949e] mb-5 leading-relaxed'
      : 'text-sm text-[#6b7280] mb-5 leading-relaxed',
    cancelBtn: isDark
      ? 'px-4 py-2 text-sm font-medium text-[#c9d1d9] bg-[#1c2432] border border-[#2d3748] rounded-[10px] hover:bg-[#222] transition-colors'
      : 'px-4 py-2 text-sm font-medium text-[#374151] bg-white border border-[#e5e7eb] rounded-[10px] hover:bg-[#f3f4f6] transition-colors',
    dangerBtn:
      'px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-[10px] hover:bg-red-500 transition-colors',
    warnIconWrap: isDark
      ? 'flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-red-950/50 text-red-400'
      : 'flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-red-50 text-red-700',
  };
}

/** Layer panel switch confirmation — neutral palette per theme (no multi-color text). */
export function getLayerSwitchConfirmTheme(isDark: boolean) {
  const cd = getConfirmDialogTheme(isDark);
  return {
    overlay: 'absolute inset-0 z-0 bg-slate-950/55 backdrop-blur-sm',
    panel: `${cd.panel.replace('max-w-sm', 'max-w-md')}`,
    iconWrap: isDark
      ? 'w-12 h-12 bg-[rgba(52,211,153,.16)] rounded-full flex items-center justify-center'
      : 'w-12 h-12 bg-[#00854014] rounded-full flex items-center justify-center',
    icon: isDark ? 'ri-alert-line text-2xl text-[#34d399]' : 'ri-alert-line text-2xl text-[#008540]',
    title: `${cd.title} text-center`,
    body: isDark
      ? 'text-sm text-[#8b949e] text-center mb-6 space-y-2'
      : 'text-sm text-[#6b7280] text-center mb-6 space-y-2',
    emphasis: isDark ? 'font-semibold text-[#e6edf3]' : 'font-semibold text-[#111827]',
    warningBox: isDark
      ? 'bg-[#1c2432] border border-[#2d3748] rounded-[10px] p-3 text-[#c9d1d9]'
      : 'bg-[#f3f4f6] border border-[#e5e7eb] rounded-[10px] p-3 text-[#374151]',
    cancelBtn: `flex-1 ${cd.cancelBtn}`,
    confirmBtn: isDark
      ? 'flex-1 px-4 py-2 text-sm font-medium text-white bg-[#008540] rounded-[10px] hover:bg-[#097a34] transition-colors'
      : 'flex-1 px-4 py-2 text-sm font-medium text-white bg-[#008540] rounded-[10px] hover:opacity-90 transition-colors',
  };
}

export function searchInputClass(theme: AppTheme): string {
  return forestSearchInputClass(theme);
}

export function desktopSearchInputClass(theme: AppTheme): string {
  return forestSearchInputClass(theme);
}

export function toastPanelClass(theme: AppTheme, type: 'success' | 'error' | 'info'): string {
  const base = 'rounded-lg shadow-2xl p-4 flex items-center space-x-3 min-w-[300px] max-w-md';
  if (theme === 'dark') {
    const border =
      type === 'error'
        ? 'border-red-700/60'
        : type === 'success'
          ? 'border-emerald-700/60'
          : 'border-gray-700';
    return `${base} bg-gray-900 text-white border ${border}`;
  }
  if (type === 'info') {
    return `${base} bg-white text-gray-900 border border-[#008540CC]`;
  }
  if (type === 'success') {
    return `${base} bg-gradient-to-r from-emerald-500 to-emerald-600 text-white`;
  }
  return `${base} bg-gradient-to-r from-red-500 to-red-600 text-white`;
}

export function toastIconWrapClass(theme: AppTheme, type: 'success' | 'error' | 'info'): string {
  const base = 'w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0';
  if (theme === 'dark') {
    if (type === 'error') return `${base} bg-red-500/20 text-red-400`;
    if (type === 'success') return `${base} bg-emerald-500/20 text-emerald-400`;
    return `${base} bg-[#00854020] text-emerald-400`;
  }
  if (type === 'info') return `${base} bg-[#00854014] text-[#008540]`;
  return `${base} bg-white/20`;
}

export function toastCloseClass(theme: AppTheme): string {
  return theme === 'dark'
    ? 'flex-shrink-0 text-gray-400 hover:text-white transition-colors'
    : 'flex-shrink-0 text-gray-500 hover:text-gray-800 transition-colors';
}

export function toastProgressTrackClass(theme: AppTheme): string {
  return theme === 'dark' ? 'h-1 bg-white/20 rounded-b-lg overflow-hidden' : 'h-1 bg-[#00854020] rounded-b-lg overflow-hidden';
}

export function toastProgressBarClass(theme: AppTheme): string {
  return theme === 'dark' ? 'h-full bg-white animate-shrink-width' : 'h-full bg-[#008540CC] animate-shrink-width';
}

/** Semantic class tokens for AnalysisPanel farm + shared surfaces. */
export function getAnalysisPanelTheme(isDark: boolean, isInlineCard = false) {
  return {
    headerBorder: isDark ? 'border-[#2d3748]' : 'border-[#e5e7eb]',
    inlineBodyBg: 'bg-transparent',
    headerActionHover: isDark
      ? 'hover:bg-white/[0.06] rounded-lg'
      : 'hover:bg-[#f3f4f6] rounded-lg',
    tabShape: isInlineCard ? 'rounded-xl' : 'rounded-md',
    tabInactive: isInlineCard
      ? isDark
        ? 'border-[#2d3748] bg-[#1c2432] text-[#c9d1d9] hover:bg-white/[0.06] hover:text-[#e6edf3]'
        : 'border-[#e5e7eb] bg-[#f3f4f6] text-[#6b7280] hover:bg-[#e5e7eb] hover:text-[#008540]'
      : isDark
        ? 'border-[#2d3748] bg-[#1c2432] text-[#8b949e] hover:bg-white/[0.06] hover:text-[#e6edf3]'
        : 'border-[#e5e7eb] bg-white text-[#6b7280] hover:bg-[#f3f4f6] hover:text-[#008540]',
    tabActive: isDark
      ? 'border-transparent text-white bg-[#008540] shadow-sm'
      : 'border-transparent text-white bg-[#008540] shadow-sm',
    card: isDark ? 'bg-[#1c2432] border-[#2d3748]' : 'bg-[#f3f4f6] border-[#e5e7eb]',
    cardSoft: isDark ? 'bg-[#1c2432]/80 border-[#2d3748]' : 'bg-white border-[#e5e7eb]',
    cardSofter: isDark ? 'bg-[#1c2432]/60 border-[#2d3748]' : 'bg-white border-[#e5e7eb]',
    cardSolid: isDark ? 'bg-[#1c2432] border-[#2d3748]' : 'bg-white border-[#e5e7eb]',
    cardHover: isDark
      ? 'hover:border-white/12 hover:bg-[#222]'
      : 'hover:border-[#d1d5db] hover:bg-[#f3f4f6]',
    statBox: isDark ? 'bg-[#1c2432]' : 'bg-[#f3f4f6] border border-[#e5e7eb]',
    heading: isDark ? 'text-[#e6edf3]' : 'text-[#111827]',
    subheading: isDark ? 'text-[#c9d1d9]' : 'text-[#374151]',
    body: isDark ? 'text-[#c9d1d9]' : 'text-[#374151]',
    muted: isDark ? 'text-[#8b949e]' : 'text-[#6b7280]',
    faint: isDark ? 'text-[#6e7681]' : 'text-[#6b7280]',
    label: isDark ? 'text-[#c9d1d9]' : 'text-[#6b7280]',
    input:
      'w-full rounded-[10px] border px-3 py-2 text-sm focus:outline-none focus:ring-1 ' +
      (isDark
        ? 'bg-[#1c2432] border-[#2d3748] text-[#e6edf3] placeholder:text-[#6e7681] focus:border-[#34d399] focus:ring-[#34d399]/40'
        : 'bg-white border-[#e5e7eb] text-[#111827] placeholder:text-[#6b7280] focus:border-[#008540] focus:ring-[#008540CC]'),
    select:
      'w-full rounded-[10px] border px-3 py-2 text-sm pr-8 appearance-none focus:outline-none focus:ring-1 ' +
      (isDark
        ? 'bg-[#1c2432] border-[#2d3748] text-[#e6edf3] focus:border-[#34d399] focus:ring-[#34d399]/40'
        : 'bg-white border-[#e5e7eb] text-[#111827] focus:border-[#008540] focus:ring-[#008540CC]'),
    selectIcon: isDark ? 'text-[#8b949e]' : 'text-[#6b7280]',
    overlay: isDark ? 'bg-[#161b22]/70' : 'bg-[#f8f5ec]/80',
    btnDisabled: isDark ? 'bg-[#253347] text-[#6e7681]' : 'bg-[#f3f4f6] text-[#6b7280]',
    accentText: isDark ? 'text-[#34d399]' : 'text-[#008540]',
    accentBorder: isDark ? 'border-[#34d399]/35' : 'border-[#00854080]',
    iconWrapVeg: isDark ? 'bg-[rgba(52,211,153,.16)] text-[#34d399]' : 'bg-[#00854014] text-[#008540]',
    iconWrapMoisture: isDark ? 'bg-blue-950/80 text-blue-400' : 'bg-blue-50 text-blue-600',
    mobileBtn: isDark
      ? 'border-[#34d399]/40 text-[#34d399] hover:bg-[rgba(52,211,153,.12)]'
      : 'border-[#00854080] text-[#008540] hover:bg-[#00854014]',
    primaryBtn: isDark
      ? 'bg-[#008540] hover:bg-[#097a34] text-white'
      : 'bg-[#008540] hover:opacity-90 text-white',
    segmentWrap: isDark ? 'bg-[#1c2432]' : 'bg-[#f3f4f6]',
    segmentActive: isDark ? 'bg-[#008540] text-white' : 'bg-[#008540] text-white',
    segmentInactive: isDark ? 'text-[#c9d1d9] hover:text-[#e6edf3]' : 'text-[#6b7280] hover:text-[#111827]',
    chartLine: isDark ? '#3b82f6' : '#008540',
    chartBg: isDark ? '#1f2937' : '#f9fafb',
    chartGrid: isDark ? '#374151' : '#e5e7eb',
    chartLabel: isDark ? '#9ca3af' : '#6b7280',
    chartAxis: isDark ? '#9ca3af' : '#6b7280',
    chartPointDefault: isDark ? '#3b82f6' : '#008540',
    chartPointSelected: '#dc2626',
    tsGenerateDisabled: isDark ? 'bg-[#253347] cursor-not-allowed text-[#6e7681]' : 'bg-[#f3f4f6] cursor-not-allowed text-[#6b7280]',
    tsProcessingShell: isDark ? 'mb-4 p-4 bg-[#1c2432] rounded-[10px] border border-[#34d399]/30' : 'mb-4 p-4 bg-[#00854014] rounded-[10px] border border-[#00854080]',
    tsProcessingTitle: isDark ? 'text-sm font-medium text-[#34d399]' : 'text-sm font-medium text-[#008540]',
    tsProcessingProgress: isDark ? 'text-xs text-[#8b949e]' : 'text-xs text-[#6b7280]',
    tsProcessingText: isDark ? 'flex items-center text-sm text-[#c9d1d9]' : 'flex items-center text-sm text-[#374151]',
    tsProgressTrack: isDark ? 'w-full bg-[#253347] rounded-full h-2 mb-3' : 'w-full bg-[#e5e7eb] rounded-full h-2 mb-3',
    tsProgressBar: isDark ? 'bg-[#008540] h-2 rounded-full transition-all duration-500 ease-out' : 'bg-gradient-to-r from-[#008540] to-[#008540CC] h-2 rounded-full transition-all duration-500 ease-out',
    tsErrorShell: isDark ? 'mb-4 p-3 bg-red-900/20 border border-red-500/30 rounded-lg' : 'mb-4 p-3 bg-red-50 border border-red-200 rounded-lg',
    tsErrorText: isDark ? 'flex items-center text-red-400' : 'flex items-center text-red-600',
    tsCloudMaskShell: isInlineCard
      ? isDark
        ? 'mb-2 px-2 py-1.5 bg-blue-900/20 rounded-md border border-blue-500/30'
        : 'mb-2 px-2 py-1.5 bg-blue-50/80 rounded-md border border-blue-200/80'
      : isDark
        ? 'mb-4 p-3 bg-blue-900/30 rounded-lg border border-blue-500/40'
        : 'mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200',
    tsCloudMaskTitle: isInlineCard
      ? isDark
        ? 'text-[10px] font-medium text-blue-300'
        : 'text-[10px] font-medium text-blue-800'
      : isDark
        ? 'text-sm font-medium text-blue-300'
        : 'text-sm font-medium text-blue-800',
    tsCloudMaskText: isDark ? 'text-xs text-blue-200 leading-relaxed' : 'text-xs text-blue-700 leading-relaxed',
    tsCloudMaskIcon: isDark ? 'text-blue-400' : 'text-blue-600',
    tsFilterShell: isInlineCard
      ? isDark
        ? 'mb-2 p-2 bg-[#1c2432] rounded-[8px] border border-[#2d3748]'
        : 'mb-2 p-2 bg-[#f3f4f6] rounded-[8px] border border-[#e5e7eb]'
      : isDark
        ? 'mb-4 p-3 bg-[#1c2432] rounded-[10px] border border-[#2d3748]'
        : 'mb-4 p-3 bg-[#f3f4f6] rounded-[10px] border border-[#e5e7eb]',
    tsFilterLabel: isInlineCard
      ? isDark
        ? 'text-[11px] font-medium text-[#e6edf3]'
        : 'text-[11px] font-medium text-[#111827]'
      : isDark
        ? 'text-sm font-medium text-[#e6edf3]'
        : 'text-sm font-medium text-[#111827]',
    tsFilterOn: isDark ? 'bg-[#008540] text-white hover:bg-[#097a34]' : 'bg-[#008540] text-white hover:opacity-90',
    tsFilterOff: isDark ? 'bg-[#253347] text-[#c9d1d9] hover:bg-[#333]' : 'bg-[#e5e7eb] text-[#6b7280] hover:bg-[#d1d5db]',
    tsInfoShell: isDark ? 'mb-2 p-2 bg-blue-900/30 rounded border border-blue-500/30' : 'mb-2 p-2 bg-blue-50 rounded border border-blue-200',
    tsInfoText: isDark ? 'text-xs text-blue-200 leading-relaxed' : 'text-xs text-blue-800 leading-relaxed',
    tsInfoIcon: isDark ? 'text-blue-400' : 'text-blue-600',
    tsStatCell: isDark ? 'bg-gray-600 rounded p-2' : 'bg-white rounded p-2 border border-gray-100',
    tsStatClearBorder: isDark ? 'border-green-400' : 'border-green-500',
    tsStatPartialBorder: isDark ? 'border-yellow-400' : 'border-yellow-500',
    tsStatCloudyBorder: isDark ? 'border-red-400' : 'border-red-500',
    tsStatCountClear: isDark ? 'text-green-400 font-semibold' : 'text-green-600 font-semibold',
    tsStatCountPartial: isDark ? 'text-yellow-400 font-semibold' : 'text-yellow-600 font-semibold',
    tsStatCountCloudy: isDark ? 'text-red-400 font-semibold' : 'text-red-600 font-semibold',
    tsStatLabel: isDark ? 'text-gray-300' : 'text-gray-600',
    tsShowingBadge: isDark ? 'text-xs text-green-400 bg-gray-600 px-2 py-1 rounded' : 'text-xs text-[#008540] bg-[#00854014] px-2 py-1 rounded',
    tsChartShell: isInlineCard
      ? isDark
        ? 'mb-2 bg-[#1c2432] rounded-[10px] p-2.5 border border-[#2d3748]'
        : 'mb-2 bg-white rounded-[10px] p-2.5 border border-[#e5e7eb] shadow-sm'
      : isDark
        ? 'mb-6 bg-[#1c2432] rounded-[14px] p-6 border border-[#2d3748]'
        : 'mb-6 bg-white rounded-[14px] p-6 border border-[#e5e7eb] shadow-sm',
    tsChartTitle: isInlineCard
      ? isDark
        ? 'text-[11px] font-medium text-[#e6edf3]'
        : 'text-[11px] font-medium text-[#111827]'
      : isDark
        ? 'text-sm font-medium text-[#e6edf3]'
        : 'text-sm font-medium text-[#111827]',
    tsChartLegend: isInlineCard
      ? isDark
        ? 'mb-2 px-1.5 py-1 bg-[#161b22] rounded-[8px] text-[10px]'
        : 'mb-2 px-1.5 py-1 bg-[#f3f4f6] rounded-[8px] text-[10px] border border-[#e5e7eb]'
      : isDark
        ? 'mb-4 p-2 bg-[#161b22] rounded-[10px] text-xs'
        : 'mb-4 p-2 bg-[#f3f4f6] rounded-[10px] text-xs border border-[#e5e7eb]',
    tsChartLegendLabel: isDark ? 'text-[#c9d1d9] font-medium' : 'text-[#374151] font-medium',
    tsChartLegendText: isDark ? 'text-[#c9d1d9]' : 'text-[#6b7280]',
    tsDetailShell: isInlineCard
      ? isDark
        ? 'mt-2 p-2.5 bg-[#161b22] rounded-[10px] border border-[#2d3748]'
        : 'mt-2 p-2.5 bg-[#f3f4f6] rounded-[10px] border border-[#e5e7eb]'
      : isDark
        ? 'mt-6 p-4 bg-[#161b22] rounded-[14px] border border-[#2d3748]'
        : 'mt-6 p-4 bg-[#f3f4f6] rounded-[14px] border border-[#e5e7eb]',
    tsDetailTitle: isInlineCard
      ? isDark
        ? 'text-[11px] font-medium text-[#e6edf3]'
        : 'text-[11px] font-medium text-[#111827]'
      : isDark
        ? 'text-sm font-medium text-[#e6edf3]'
        : 'text-sm font-medium text-[#111827]',
    tsDetailValue: isInlineCard
      ? isDark
        ? 'text-lg font-bold text-[#34d399]'
        : 'text-lg font-bold text-[#008540]'
      : isDark
        ? 'text-2xl font-bold text-[#34d399]'
        : 'text-2xl font-bold text-[#008540]',
    tsDetailMuted: isDark ? 'text-xs text-gray-400' : 'text-xs text-gray-500',
    tsDetailSubEm: isDark ? 'text-gray-300' : 'text-gray-700',
    tsDetailDivider: isDark ? 'border-gray-600' : 'border-gray-200',
    tsTooltipShell: isDark ? 'absolute left-0 bottom-full mb-2 w-64 p-3 bg-gray-800 border border-gray-600 rounded-lg shadow-lg z-50 text-xs text-gray-200' : 'absolute left-0 bottom-full mb-2 w-64 p-3 bg-white border border-gray-200 rounded-lg shadow-lg z-50 text-xs text-gray-700',
    tsTooltipTitle: isDark ? 'font-semibold text-yellow-400 mb-1' : 'font-semibold text-amber-700 mb-1',
    tsTooltipBody: isDark ? 'text-gray-300' : 'text-gray-600',
    tsTooltipClose: isDark ? 'mt-2 text-blue-400 hover:text-blue-300 text-xs' : 'mt-2 text-[#008540] hover:opacity-80 text-xs',
    tsNullIcon: isDark ? 'text-yellow-400 hover:text-yellow-300' : 'text-amber-600 hover:text-amber-700',
    tsOpacityShell: isInlineCard
      ? isDark
        ? 'mb-2 px-2 py-1.5 bg-gray-800 rounded-md'
        : 'mb-2 px-2 py-1.5 bg-gray-50 rounded-md border border-gray-200'
      : isDark
        ? 'mb-3 p-3 bg-gray-800 rounded-lg'
        : 'mb-3 p-3 bg-gray-50 rounded-lg border border-gray-200',
    tsRangeClass: isDark ? 'w-full h-2 rounded-lg appearance-none cursor-pointer time-series-range time-series-range--dark' : 'w-full h-2 rounded-lg appearance-none cursor-pointer time-series-range time-series-range--light',
    tsExportBtn: isDark ? 'px-2 py-1 text-white text-xs rounded flex items-center bg-blue-600 hover:bg-blue-700' : 'px-2 py-1 text-white text-xs rounded flex items-center bg-[#008540] hover:opacity-90',
    tsExportBtnDisabled: isDark ? 'bg-blue-400 cursor-not-allowed' : 'bg-[#008540]/50 cursor-not-allowed',
    tsMultiChartShell: isDark ? 'mb-4 p-3 bg-[#1c2432] rounded-[10px] border border-[#2d3748]' : 'mb-4 p-3 bg-white rounded-[10px] border border-[#e5e7eb]',
    tsMultiChartTitle: isDark ? 'text-xs font-semibold text-[#e6edf3]' : 'text-xs font-semibold text-[#111827]',
    tsMultiChartIcon: isDark ? 'text-[#34d399]' : 'text-[#008540]',
    tsMultiFullscreenBtn: isDark ? 'text-[#34d399] hover:text-[#6ee7b7] transition-colors p-1 rounded hover:bg-white/[0.06]' : 'text-[#008540] hover:opacity-80 transition-colors p-1 rounded hover:bg-[#00854014]',
    tsMultiLegend: isDark ? 'text-[#8b949e]' : 'text-[#6b7280]',
    tsMultiRunDisabled: isDark ? 'bg-[#253347] text-[#6e7681] cursor-not-allowed' : 'bg-[#f3f4f6] text-[#6b7280] cursor-not-allowed',
    tsMultiRunActive: isDark ? 'bg-[#008540] hover:bg-[#097a34] text-white' : 'bg-[#008540] hover:opacity-90 text-white',
    tsMultiRunWait: isDark ? 'bg-[#008540]/60 text-white cursor-wait' : 'bg-[#008540]/60 text-white cursor-wait',
    infoNote: isDark
      ? 'mb-4 p-3 rounded-[10px] border border-[#2d3748] bg-[#1c2432]'
      : 'mb-4 p-3 rounded-[10px] border border-[#e5e7eb] bg-[#f3f4f6]',
    infoNoteIcon: isDark ? 'text-[#34d399]' : 'text-[#008540]',
    infoNoteTitle: isDark ? 'text-[#e6edf3]' : 'text-[#111827]',
    infoNoteBody: isDark ? 'text-[#c9d1d9]' : 'text-[#374151]',
  };
}

export type ResultStripAccent = 'emerald' | 'red' | 'blue';

/** Bottom result strips on HomePage + field-level forecast summary cards. */
export function getAnalysisResultStripTheme(isDark: boolean, accent: ResultStripAccent) {
  const accentBorder = {
    emerald: isDark ? 'border-emerald-500/50' : 'border-[#00854080]',
    red: isDark ? 'border-red-500/50' : 'border-red-300',
    blue: isDark ? 'border-blue-500/50' : 'border-blue-300',
  }[accent];

  const title = {
    emerald: isDark ? 'text-emerald-300' : 'text-[#008540]',
    red: isDark ? 'text-red-300' : 'text-red-700',
    blue: isDark ? 'text-blue-300' : 'text-blue-800',
  }[accent];

  const icon = {
    emerald: isDark ? 'text-emerald-400' : 'text-[#008540]',
    red: isDark ? 'text-red-400' : 'text-red-600',
    blue: isDark ? 'text-blue-400' : 'text-blue-600',
  }[accent];

  const badge = {
    emerald: isDark
      ? 'bg-emerald-600/50 text-emerald-200 border border-emerald-500/50'
      : 'bg-[#00854014] text-[#008540] border border-[#00854080]',
    red: isDark
      ? 'bg-red-600/50 text-red-200 border border-red-500/50'
      : 'bg-red-50 text-red-700 border border-red-200',
    blue: isDark
      ? 'bg-blue-600/50 text-blue-200 border border-blue-500/50'
      : 'bg-blue-50 text-blue-800 border border-blue-200',
  }[accent];

  const actionHover = {
    emerald: isDark ? 'hover:bg-emerald-500/20 hover:border-emerald-500/50' : 'hover:bg-[#00854014] hover:border-[#00854080]',
    red: isDark ? 'hover:bg-red-500/20 hover:border-red-500/50' : 'hover:bg-red-50 hover:border-red-300',
    blue: isDark ? 'hover:bg-blue-500/30 hover:border-blue-400' : 'hover:bg-blue-50 hover:border-blue-300',
  }[accent];

  const mobileTabActive = {
    emerald: isDark ? 'bg-emerald-600/80 text-white border-emerald-500' : 'bg-[#008540] text-white border-[#008540]',
    red: isDark ? 'bg-red-600/80 text-white border-red-500' : 'bg-red-600 text-white border-red-500',
    blue: isDark ? 'bg-blue-600/80 text-white border-blue-500' : 'bg-blue-600 text-white border-blue-500',
  }[accent];

  const statRing = {
    emerald: isDark
      ? 'bg-gradient-to-br from-emerald-500/20 to-emerald-600/20 border-2 border-emerald-500/50'
      : 'bg-gradient-to-br from-[#00854014] to-[#00854024] border-2 border-[#00854080]',
    red: isDark
      ? 'bg-gradient-to-br from-red-500/20 to-red-600/20 border-2 border-red-500/50'
      : 'bg-gradient-to-br from-red-50 to-red-100 border-2 border-red-200',
    blue: isDark
      ? 'bg-gradient-to-br from-blue-500/20 to-blue-600/20 border-2 border-blue-500/50'
      : 'bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-blue-200',
  }[accent];

  const statValue = {
    emerald: isDark ? 'text-emerald-400' : 'text-[#008540]',
    red: isDark ? 'text-red-400' : 'text-red-600',
    blue: isDark ? 'text-blue-400' : 'text-blue-700',
  }[accent];

  const chartBar = accent === 'emerald' ? (isDark ? '#10b981' : '#008540') : accent === 'red' ? (isDark ? '#f87171' : '#dc2626') : (isDark ? '#60a5fa' : '#2563eb');

  return {
    shell: isDark
      ? `flex flex-col bg-[rgba(22,27,34,.95)] border-t ${accentBorder} shadow-[0_1px_2px_rgba(0,0,0,.4)] min-h-0 relative`
      : `flex flex-col bg-[rgba(248,245,236,.96)] border-t ${accentBorder} shadow-[0_1px_2px_rgba(20,25,20,.04)] min-h-0 relative`,
    title,
    icon,
    badge,
    muted: isDark ? 'text-gray-400' : 'text-gray-600',
    body: isDark ? 'text-gray-300' : 'text-gray-700',
    actionBtn: `p-2 rounded-lg transition-all duration-300 border border-transparent hover:scale-110 disabled:opacity-50 disabled:cursor-wait ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'} ${actionHover}`,
    closeBtn: `p-2 rounded-lg transition-all duration-300 border border-transparent hover:scale-110 hover:rotate-90 ${isDark ? 'text-gray-400 hover:text-white hover:bg-red-500/20 hover:border-red-500/50' : 'text-gray-500 hover:text-red-700 hover:bg-red-50 hover:border-red-300'}`,
    navBtn: `p-2 rounded-lg transition-all duration-200 border border-transparent ${isDark ? 'text-gray-400 hover:text-white hover:bg-blue-500/30 hover:border-blue-400' : 'text-gray-500 hover:text-blue-700 hover:bg-blue-50 hover:border-blue-300'}`,
    mobileTabActive: `py-2 px-2 rounded-lg text-xs font-medium transition-colors border ${mobileTabActive}`,
    mobileTabInactive: isDark
      ? 'py-2 px-2 rounded-lg text-xs font-medium transition-colors border bg-gray-800/80 text-gray-300 border-gray-700 hover:bg-gray-700/80'
      : 'py-2 px-2 rounded-lg text-xs font-medium transition-colors border bg-gray-100 text-gray-600 border-gray-200 hover:bg-gray-200',
    statRing,
    statValue,
    statUnit: isDark ? 'text-gray-400' : 'text-gray-500',
    statLabel: isDark ? 'text-gray-400' : 'text-gray-600',
    infoCard: isDark
      ? 'rounded-lg border border-gray-700/50 bg-gray-800/60'
      : 'rounded-lg border border-gray-200 bg-gray-50',
    infoDivider: isDark ? 'border-gray-700/50' : 'border-gray-200',
    exportBtn: accent === 'emerald'
      ? isDark
        ? 'bg-emerald-700/80 hover:bg-emerald-600 text-white border border-emerald-500/50'
        : 'bg-[#008540] hover:opacity-90 text-white border border-[#00854080]'
      : isDark
        ? 'bg-emerald-700/80 hover:bg-emerald-600 text-white border border-emerald-500/50'
        : 'bg-[#008540] hover:opacity-90 text-white border border-[#00854080]',
    primaryBtn: isDark
      ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
      : 'bg-[#008540] hover:opacity-90 text-white',
    tableHead: isDark ? 'bg-gray-800/80 sticky top-0' : 'bg-gray-100 sticky top-0',
    tableHeadText: isDark ? 'text-gray-300' : 'text-gray-600',
    tableBody: isDark ? 'bg-gray-900/50 divide-y divide-gray-700' : 'bg-white divide-y divide-gray-200',
    tableDivide: isDark ? 'divide-gray-700' : 'divide-gray-200',
    tableRow: isDark ? 'hover:bg-gray-800/50' : 'hover:bg-gray-50',
    tableCell: isDark ? 'text-gray-200' : 'text-gray-900',
    tableCellMuted: isDark ? 'text-gray-300' : 'text-gray-600',
    tableEvent: isDark ? 'text-blue-300' : 'text-blue-700',
    chartGrid: isDark ? '#374151' : '#e5e7eb',
    chartAxis: isDark ? '#6b7280' : '#9ca3af',
    chartLabel: isDark ? '#9ca3af' : '#6b7280',
    chartValue: isDark ? '#ffffff' : '#111827',
    chartBar,
    summaryShell: isDark ? 'p-3 rounded-lg bg-gray-800 border border-gray-700' : 'p-3 rounded-lg bg-white border border-gray-200 shadow-sm',
    summaryTitle: isDark ? 'text-xs font-semibold text-gray-200' : 'text-xs font-semibold text-gray-900',
    summaryMetric: isDark ? 'text-gray-400' : 'text-gray-600',
    summaryValue: isDark ? 'text-emerald-300' : 'text-[#008540]',
    fieldCard: isDark ? 'p-3 bg-gray-800 rounded-lg border border-gray-700' : 'p-3 bg-gray-50 rounded-lg border border-gray-200',
    fieldName: isDark ? 'text-sm font-semibold text-gray-100' : 'text-sm font-semibold text-gray-900',
    select: isDark
      ? 'w-full pl-8 pr-3 py-2 text-[11px] rounded-lg bg-gray-800 border border-gray-600 text-white hover:bg-gray-800/80 focus:ring-2 focus:ring-emerald-500 focus:border-transparent'
      : 'w-full pl-8 pr-3 py-2 text-[11px] rounded-lg bg-white border border-gray-200 text-gray-900 hover:bg-gray-50 focus:ring-2 focus:ring-[#008540CC] focus:border-[#008540]',
    iconWrap: isDark ? 'p-2 rounded-lg bg-emerald-500/20' : 'p-2 rounded-lg bg-[#00854014]',
    pdfBtn: isDark
      ? 'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[10px] font-medium bg-emerald-600/30 text-emerald-200 border border-emerald-500/50 hover:bg-emerald-600/50 disabled:opacity-50 disabled:cursor-wait'
      : 'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[10px] font-medium bg-[#00854014] text-[#008540] border border-[#00854080] hover:bg-[#00854024] disabled:opacity-50 disabled:cursor-wait',
    submitDisabled: isDark ? 'bg-gray-700 text-gray-500 cursor-not-allowed shadow-none' : 'bg-gray-200 text-gray-500 cursor-not-allowed shadow-none',
    submitActive: isDark
      ? 'bg-[#008540] text-white hover:bg-[#097a34] hover:shadow-lg hover:scale-[1.02] active:scale-[0.98]'
      : 'bg-[#008540] text-white hover:opacity-90 hover:shadow-lg hover:scale-[1.02] active:scale-[0.98]',
    vizLabel: isDark ? 'text-xs font-medium text-gray-400' : 'text-xs font-medium text-gray-600',
    imageBorder: isDark ? 'border-gray-600' : 'border-gray-200',
    modalOverlay: isDark ? 'bg-black/80' : 'bg-black/50',
    modalPanel: isDark ? 'bg-gray-900 border border-gray-700' : 'bg-white border border-gray-200',
    modalTitle: isDark ? 'text-white' : 'text-gray-900',
  };
}

export function getImageComparisonTheme(isDark: boolean) {
  return {
    shell: isDark
      ? 'relative w-full rounded border border-gray-700/80 bg-gray-800/40 p-1.5 select-none'
      : 'relative w-full rounded border border-gray-200 bg-gray-50/80 p-1.5 select-none',
    track: isDark
      ? 'relative h-7 rounded overflow-hidden cursor-ew-resize bg-gray-700/60'
      : 'relative h-7 rounded overflow-hidden cursor-ew-resize bg-gray-200/80',
    beforeTint: 'absolute top-0 left-0 h-full bg-blue-500/15 transition-all pointer-events-none',
    afterTint: 'absolute top-0 right-0 h-full bg-orange-500/15 transition-all pointer-events-none',
    handle: isDark
      ? 'absolute top-0 h-full w-px bg-gray-400/90 cursor-ew-resize z-10'
      : 'absolute top-0 h-full w-px bg-gray-500/80 cursor-ew-resize z-10',
    grip: isDark
      ? 'absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-5 h-5 bg-gray-800 rounded-full shadow flex items-center justify-center border border-gray-600'
      : 'absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-5 h-5 bg-white rounded-full shadow flex items-center justify-center border border-gray-300',
    gripLine: isDark ? 'w-px h-2.5 bg-gray-500' : 'w-px h-2.5 bg-gray-400',
    footer: isDark
      ? 'flex items-center justify-between mt-1 text-[10px] text-gray-500'
      : 'flex items-center justify-between mt-1 text-[10px] text-gray-500',
    pctBefore: isDark ? 'text-gray-400' : 'text-gray-600',
    pctAfter: isDark ? 'text-gray-400' : 'text-gray-600',
    hint: isDark ? 'text-gray-500' : 'text-gray-400',
  };
}

/** On-map layer / index legend overlays (left of the map, beside zoom). */
export function getMapLegendTheme(isDark: boolean) {
  return {
    /** Map-relative: left side, to the right of the zoom stack so they don't overlap. */
    anchor:
      'absolute left-[4.25rem] top-4 z-10 max-w-xs w-auto',
    anchorCompact: 'absolute left-[4.25rem] top-4 z-10 w-[10.5rem] sm:w-44',
    anchorStacked: 'absolute left-[4.25rem] top-[11.5rem] z-10 max-w-xs w-auto',
    /** Viewport-fixed (embedded LayerTogglePanel) — just right of the 22rem left dock, clear of the zoom stack. */
    anchorFixed:
      'fixed left-[4.25rem] bottom-24 z-30 max-w-[150px] sm:max-w-[170px] lg:left-[calc(22rem+4.75rem)] lg:top-[5.75rem] lg:bottom-auto',
    shell: isDark
      ? 'rounded-[14px] border-0 bg-[rgba(22,27,34,.95)] p-3 text-[#e6edf3] shadow-[0_1px_2px_rgba(0,0,0,.4),0_10px_26px_-14px_rgba(0,0,0,.7)] backdrop-blur-[6px]'
      : 'rounded-[14px] border-0 bg-[rgba(248,245,236,.96)] p-3 text-[#111827] shadow-[0_1px_2px_rgba(20,25,20,.04),0_10px_24px_-14px_rgba(20,25,20,.18)] backdrop-blur-[6px]',
    shellCompact: isDark
      ? 'rounded-[14px] border-0 bg-[rgba(22,27,34,.95)] p-2 sm:p-3 text-[#e6edf3] shadow-[0_1px_2px_rgba(0,0,0,.4),0_10px_26px_-14px_rgba(0,0,0,.7)] backdrop-blur-[6px]'
      : 'rounded-[14px] border-0 bg-[rgba(248,245,236,.96)] p-2 sm:p-3 text-[#111827] shadow-[0_1px_2px_rgba(20,25,20,.04),0_10px_24px_-14px_rgba(20,25,20,.18)] backdrop-blur-[6px]',
    title: isDark ? 'text-xs font-semibold text-[#e6edf3]' : 'text-xs font-semibold text-[#111827]',
    titleSm: isDark ? 'text-[10px] sm:text-xs font-semibold text-[#e6edf3]' : 'text-[10px] sm:text-xs font-semibold text-[#111827]',
    subtitle: isDark ? 'text-[8px] sm:text-[9px] font-medium text-[#8b949e]' : 'text-[8px] sm:text-[9px] font-medium text-[#6b7280]',
    body: isDark ? 'text-xs text-[#8b949e]' : 'text-xs text-[#6b7280]',
    bodySm: isDark ? 'text-[8px] sm:text-[9px] text-[#8b949e]' : 'text-[8px] sm:text-[9px] text-[#6b7280]',
    label: isDark ? 'text-sm text-[#c9d1d9]' : 'text-sm text-[#374151]',
    labelSm: isDark ? 'text-[9px] sm:text-[10px] text-[#c9d1d9] font-medium' : 'text-[9px] sm:text-[10px] text-[#374151] font-medium',
    value: isDark ? 'text-xs font-semibold text-[#34d399]' : 'text-xs font-semibold text-[#008540]',
    infoCard: isDark ? 'rounded-[10px] bg-[#1c2432] p-2' : 'rounded-[10px] bg-[#f3f4f6] p-2',
    infoTitle: isDark ? 'text-xs font-semibold text-[#e6edf3]' : 'text-xs font-semibold text-[#111827]',
    gradientBorder: isDark
      ? 'h-6 sm:h-8 w-full rounded-[10px] overflow-hidden'
      : 'h-6 sm:h-8 w-full rounded-[10px] overflow-hidden',
    colorBarBorder: isDark
      ? 'relative h-3 sm:h-4 rounded-full overflow-hidden'
      : 'relative h-3 sm:h-4 rounded-full overflow-hidden',
    swatchBorder: isDark
      ? 'w-3 h-3 rounded border-0 flex-shrink-0'
      : 'w-3 h-3 rounded border-0 flex-shrink-0',
    rangeText: isDark
      ? 'flex justify-between font-mono text-[8px] sm:text-[9px] text-[#8b949e]'
      : 'flex justify-between font-mono text-[8px] sm:text-[9px] text-[#6b7280]',
    monoRange: isDark
      ? 'flex justify-between font-mono text-xs text-[#8b949e]'
      : 'flex justify-between font-mono text-xs text-[#6b7280]',
    divider: isDark ? 'border-t border-[#2d3748]' : 'border-t border-[#e5e7eb]',
    burnNo: isDark ? 'text-sm text-[#c9d1d9]' : 'text-sm text-[#374151]',
    burnYes: isDark ? 'text-sm text-[#34d399] font-medium' : 'text-sm text-red-600 font-medium',
    noBurnSwatch: isDark
      ? 'w-6 h-6 rounded bg-[#161b22] ring-1 ring-white/10'
      : 'w-6 h-6 rounded bg-white ring-1 ring-[#e5e7eb]',
    totalLabel: isDark ? 'text-[#8b949e] font-medium' : 'text-[#6b7280] font-medium',
    totalValue: isDark ? 'text-[#e6edf3] font-semibold' : 'text-[#111827] font-semibold',
    imgBorder: isDark ? 'max-w-full h-auto rounded-[10px]' : 'max-w-full h-auto rounded-[10px]',
    infoBtn: isDark
      ? 'text-[#8b949e] hover:text-[#34d399] hover:bg-white/[0.06]'
      : 'text-[#6b7280] hover:text-[#008540] hover:bg-[#f3f4f6]',
  };
}

/** Large-scale layer picker (docked left column) — paper / gold, matching the rest of the chrome. */
export function getLayerToggleTheme(isDark: boolean) {
  const hairline = isDark ? 'border-[#2d3748]' : 'border-[#e5e7eb]';
  const paper = isDark ? 'bg-[#161b22]' : 'bg-white';
  const raised = isDark ? 'bg-[#1c2432]' : 'bg-white';
  return {
    panel: isDark ? 'bg-[#161b22] border-0' : 'bg-white border-0',
    tabRail: isDark
      ? 'bg-[#161b22] border-[#2d3748]'
      : 'bg-[#f5f3ec] border-[#e5e7eb]',
    tabActive: isDark
      ? 'bg-[#008540] text-white shadow-sm'
      : 'bg-[#008540] text-white shadow-sm',
    tabIdle: isDark
      ? 'bg-[#1c2432] text-[#c9d1d9] border border-[#2d3748] hover:border-white/12 hover:text-[#e6edf3]'
      : 'bg-white text-[#374151] border border-[#e5e7eb] hover:border-[#d1d5db] hover:bg-[#f3f4f6]',
    content: isDark ? 'bg-[#161b22]' : 'bg-white',
    /** Idle / open section card — same hairline as the fields sidebar, no rainbow borders. */
    section: `rounded-[14px] border ${hairline} ${paper}`,
    sectionHover: isDark ? 'hover:bg-white/[0.04]' : 'hover:bg-[#f3f4f6]',
    layerSelected: isDark
      ? 'border-[#2d3748] bg-[rgba(52,211,153,.12)]'
      : 'border-[#e5e7eb] bg-[rgba(0,133,64,.08)]',
    layerIdle: isDark
      ? 'border-[#2d3748] bg-[#1c2432] hover:border-white/12 hover:bg-[#222]'
      : 'border-[#e5e7eb] bg-white hover:border-[#d1d5db] hover:bg-[#f3f4f6]',
    radioOn: isDark ? 'border-[#34d399]' : 'border-[#008540]',
    radioDot: isDark ? 'bg-[#008540]' : 'bg-[#008540]',
    selectedText: isDark ? 'text-[#34d399]' : 'text-[#008540]',
    primaryBtn: isDark
      ? 'bg-[#008540] hover:bg-[#097a34] text-white'
      : 'bg-[#008540] hover:opacity-90 text-white',
    emptyIconWrap: isDark
      ? 'bg-[rgba(52,211,153,.16)] text-[#34d399]'
      : 'bg-[rgba(0,133,64,.12)] text-[#008540]',
    heading: isDark ? 'text-[#e6edf3]' : 'text-[#111827]',
    muted: isDark ? 'text-[#8b949e]' : 'text-[#6b7280]',
    body: isDark ? 'text-[#c9d1d9]' : 'text-[#374151]',
    sectionActive: `rounded-[14px] border ${hairline} ${paper}`,
    sectionIcon: isDark ? 'text-[#34d399]' : 'text-[#008540]',
    chevron: isDark ? 'text-[#8b949e]' : 'text-[#6b7280]',
    input: isDark
      ? 'bg-[#1c2432] border-[#2d3748] text-[#e6edf3]'
      : 'bg-white border-[#e5e7eb] text-[#111827]',
    toggleOn: isDark
      ? 'border border-[#2d3748] bg-[rgba(52,211,153,.12)]'
      : 'border border-[#e5e7eb] bg-[rgba(0,133,64,.08)]',
    toggleOff: isDark
      ? 'border border-[#2d3748] bg-[#1c2432] hover:bg-[#222]'
      : 'border border-[#e5e7eb] bg-white hover:bg-[#f3f4f6]',
    infoNote: isDark
      ? 'rounded-[10px] border border-[#2d3748] bg-[#1c2432] text-[#c9d1d9]'
      : 'rounded-[10px] border border-[#e5e7eb] bg-[#f3f4f6] text-[#374151]',
    featured: isDark
      ? 'bg-[rgba(52,211,153,.16)] text-[#34d399]'
      : 'bg-[rgba(0,133,64,.12)] text-[#008540]',
    range: isDark
      ? 'w-full h-1.5 bg-[#253347] rounded-lg appearance-none cursor-pointer accent-[#34d399]'
      : 'w-full h-1.5 bg-[#e5e7eb] rounded-lg appearance-none cursor-pointer accent-[#008540]',
    nested: `rounded-[10px] border ${hairline} ${raised}`,
  };
}

/** Semantic class tokens for the fields sidebar. */
export function getSidebarTheme(isDark: boolean) {
  return {
    headerBorder: isDark ? 'border-[#2d3748]' : 'border-[#e5e7eb]',
    headerHover: isDark ? 'hover:bg-white/[0.06]' : 'hover:bg-[#f3f4f6]',
    headerIcon: isDark ? 'text-[#c9d1d9]' : 'text-[#6b7280]',
    eyebrow:
      'flex items-center gap-1.5 font-mono text-[11px] font-semibold uppercase tracking-[0.11em] text-[#6b7280]',
    eyebrowDark: 'flex items-center gap-1.5 font-mono text-[11px] font-semibold uppercase tracking-[0.11em] text-[#8b949e]',
    countPill: isDark
      ? 'font-mono text-[11px] font-semibold text-[#34d399] bg-[rgba(52,211,153,.16)] rounded-full px-2.5 py-0.5'
      : 'font-mono text-[11px] font-semibold text-[#008540] bg-[rgba(0,133,64,.12)] rounded-full px-2.5 py-0.5',
    sectionTitle: isDark
      ? 'text-sm font-medium text-[#e6edf3]'
      : 'text-xs font-semibold uppercase tracking-wide text-[#6b7280]',
    toolCard: isDark
      ? 'bg-[#161b22] text-[#e6edf3] hover:bg-[#1c2432] border border-[#2d3748] hover:border-white/12'
      : 'bg-white text-[#111827] border border-[#e5e7eb] hover:border-[#d1d5db] hover:bg-[#f3f4f6]',
    toolCardActive: isDark
      ? 'bg-[#008540] text-white shadow-sm border border-transparent'
      : 'bg-[#008540] text-white shadow-sm border border-transparent',
    toolIconWrap: isDark
      ? 'bg-[rgba(52,211,153,.16)] text-[#34d399]'
      : 'bg-[rgba(0,133,64,.12)] text-[#008540]',
    fullBtn: isDark
      ? 'bg-[#161b22] text-[#c9d1d9] hover:bg-[#1c2432] hover:text-[#e6edf3] border border-[#2d3748]'
      : 'bg-white text-[#111827] border border-[#e5e7eb] hover:border-[#d1d5db] hover:bg-[#f3f4f6]',
    fullBtnActive: isDark
      ? 'bg-[#008540] border-transparent text-white'
      : 'bg-[#008540] border-[#008540] text-white',
    menuDropdown: isDark
      ? 'border-[#2d3748] bg-[#161b22]'
      : 'border-[#e5e7eb] bg-white shadow-lg',
    menuItem: isDark ? 'hover:bg-white/[0.06] text-[#e6edf3]' : 'hover:bg-[#f3f4f6] text-[#111827]',
    menuItemMuted: isDark ? 'text-[#8b949e]' : 'text-[#6b7280]',
    infoBanner: isDark
      ? 'bg-[rgba(52,211,153,.12)] border border-[#34d399]/35 text-[#c9d1d9]'
      : 'bg-[#00854014] border border-[#00854080] text-[#374151]',
    infoIcon: isDark ? 'text-[#34d399]' : 'text-[#008540]',
    input:
      'w-full rounded-[10px] border py-1.5 text-[11px] focus:outline-none focus:ring-0 ' +
      (isDark
        ? 'bg-[#1c2432] border-[#2d3748] text-[#e6edf3] placeholder:text-[#8b949e] focus:border-white/12'
        : 'bg-[#f3f4f6] border-[#e5e7eb] text-[#111827] placeholder:text-[#6b7280] focus:border-[#d1d5db]'),
    fieldCard: isDark
      ? 'bg-[#161b22] hover:border-white/12 border border-[#2d3748]'
      : 'bg-white hover:border-[#d1d5db] border border-[#e5e7eb]',
    fieldCardSelected: isDark
      ? 'bg-[#161b22] border border-white/12'
      : 'bg-white border border-[#d1d5db]',
    fieldCardDemo: isDark
      ? 'bg-[#161b22] border border-[#34d399]/35'
      : 'bg-[#f3f4f6] border border-[#e5e7eb]',
    fieldName: isDark ? 'text-[#e6edf3] font-medium' : 'text-[#111827] font-semibold',
    fieldNameSelected: isDark ? 'text-[#e6edf3] font-semibold' : 'text-[#008540] font-semibold',
    fieldSub: isDark ? 'text-[#8b949e]' : 'text-[#6b7280]',
    fieldSubSelected: isDark ? 'text-[#c9d1d9]' : 'text-[#374151]',
    fieldId: isDark ? 'text-[#34d399]' : 'text-[#008540]',
    fieldIdSelected: isDark ? 'text-[#34d399]' : 'text-[#008540]',
    countText: isDark ? 'text-[#8b949e]' : 'text-[#6b7280]',
    linkText: isDark ? 'text-[#34d399] hover:text-[#6ee7b7]' : 'text-[#008540] hover:opacity-80',
    pageDisabled: isDark ? 'bg-[#253347] text-[#6e7681]' : 'bg-[#f3f4f6] text-[#6b7280]',
    pageBtn: isDark
      ? 'bg-[#008540] hover:bg-[#097a34] text-white'
      : 'bg-[#008540] hover:opacity-90 text-white',
    pageInfo: isDark ? 'text-[#34d399]' : 'text-[#6b7280]',
    emptyText: isDark ? 'text-[#8b949e]' : 'text-[#6b7280]',
    deleteIcon: isDark ? 'text-[#6e7681]' : 'text-[#6b7280] hover:text-[#111827]',
    panel: isDark
      ? 'rounded-[14px] border border-[#2d3748] bg-[#161b22]'
      : 'rounded-[14px] border border-[#e5e7eb] bg-white',
    panelTitle: isDark ? 'text-sm font-medium text-[#34d399]' : 'text-sm font-medium text-[#008540]',
    panelMuted: isDark ? 'text-[#8b949e] hover:text-[#e6edf3]' : 'text-[#6b7280] hover:text-[#111827]',
    panelInner: isDark
      ? 'space-y-2 rounded-[10px] border border-[#2d3748] bg-[#161b22] p-2.5'
      : 'space-y-2 rounded-[10px] border border-[#e5e7eb] bg-[#f3f4f6] p-2.5',
    panelLabel: isDark ? 'text-[11px] font-medium text-gray-300' : 'text-[11px] font-medium text-gray-600',
    panelText: isDark ? 'text-[11px] leading-snug text-gray-300' : 'text-[11px] leading-snug text-gray-600',
    panelTextStrong: isDark ? 'font-medium text-white' : 'font-medium text-gray-900',
    segmentWrap: isDark ? 'flex overflow-hidden rounded-[10px] border border-[#2d3748] text-[11px]' : 'flex overflow-hidden rounded-[10px] border border-[#e5e7eb] text-[11px]',
    segmentInactive: isDark ? 'bg-[#1c2432] text-[#c9d1d9] hover:bg-[#222]' : 'bg-white text-[#6b7280] hover:bg-[#f3f4f6]',
    segmentActive: isDark ? 'bg-[#008540] text-white' : 'bg-[#008540] text-white',
    drawBanner: isDark
      ? 'bg-[rgba(52,211,153,.12)] border border-[#34d399]/35 rounded-[10px] p-3'
      : 'bg-[#00854014] border border-[#00854080] rounded-[10px] p-3',
    drawBannerTitle: isDark ? 'flex items-center text-[#34d399] text-xs mb-2' : 'flex items-center text-[#008540] text-xs mb-2 font-medium',
    drawBannerText: isDark ? 'text-xs text-[#c9d1d9]' : 'text-xs text-[#374151]',
    primaryBtn: isDark
      ? 'bg-[#008540] hover:bg-[#097a34] text-white disabled:cursor-not-allowed disabled:bg-[#253347] disabled:text-[#6e7681]'
      : 'bg-[#008540] hover:opacity-90 text-white disabled:cursor-not-allowed disabled:bg-[#f3f4f6] disabled:text-[#6b7280]',
    secondaryBtn: isDark
      ? 'bg-[#1c2432] text-[#c9d1d9] hover:bg-[#222] border border-[#2d3748]'
      : 'bg-white text-[#374151] hover:bg-[#f3f4f6] border border-[#e5e7eb]',
    quietChip: isDark
      ? 'border border-[#2d3748] bg-[#1c2432] text-[#c9d1d9] hover:border-white/12 hover:text-[#34d399]'
      : 'border border-[#e5e7eb] bg-white text-[#374151] hover:border-[#d1d5db] hover:text-[#008540]',
    mobileAppShell: isDark
      ? 'mb-4 rounded-[20px] border-0 bg-[#1f1f1f] p-[18px]'
      : 'mb-4 rounded-[20px] border-0 bg-[#008540] p-[18px]',
    mobileAppQrWrap: 'bg-white',
    mobileAppIcon: isDark ? 'text-[#34d399]' : 'text-[#34d399]',
    mobileAppTitle: isDark ? 'text-white font-sans' : 'text-white font-sans',
    mobileAppText: isDark ? 'text-[#8b949e]' : 'text-[#cfe0d3]',
    mobileAppLink: isDark
      ? 'text-[#34d399] hover:opacity-90'
      : 'text-[#34d399] hover:opacity-90',
    mobileAppExpandBtn: isDark
      ? 'absolute top-1 right-1 p-1 rounded-md text-[#8b949e] hover:text-white hover:bg-white/10 transition-colors'
      : 'absolute top-1 right-1 p-1 rounded-md text-white/70 hover:text-white hover:bg-white/10 transition-colors',
    mobileAppModalOverlay: isDark ? 'bg-black/85' : 'bg-black/60',
    mobileAppModalPanel: isDark
      ? 'bg-gray-900 border border-gray-700 rounded-2xl shadow-2xl'
      : 'bg-white border border-gray-200 rounded-2xl shadow-2xl',
    mobileAppModalTitle: isDark ? 'text-lg font-semibold text-gray-100' : 'text-lg font-semibold text-gray-900',
    mobileAppModalText: isDark ? 'text-sm text-gray-400' : 'text-sm text-gray-600',
    mobileAppModalClose: isDark
      ? 'p-2 rounded-lg text-gray-400 hover:text-white hover:bg-gray-800 transition-colors'
      : 'p-2 rounded-lg text-gray-500 hover:text-gray-800 hover:bg-gray-100 transition-colors',
  };
}

export function getModalTheme(isDark: boolean) {
  return {
    overlay: isDark ? 'bg-black/70' : 'bg-black/40',
    dialog: isDark
      ? 'rounded-[20px] border border-[#2d3748] bg-[#161b22] text-[#e6edf3] shadow-[0_1px_2px_rgba(0,0,0,.4),0_10px_26px_-14px_rgba(0,0,0,.7)]'
      : 'rounded-[20px] border border-[#e5e7eb] bg-white text-[#111827] shadow-[0_1px_2px_rgba(20,25,20,.04),0_10px_24px_-14px_rgba(20,25,20,.18)]',
    cancelBtn: isDark
      ? 'text-[#c9d1d9] bg-[#1c2432] border border-[#2d3748] hover:bg-[#222] rounded-[10px]'
      : 'text-[#374151] bg-white border border-[#e5e7eb] hover:bg-[#f3f4f6] rounded-[10px]',
    note: isDark
      ? 'rounded-[10px] border border-[#2d3748] bg-[#1c2432] text-[#c9d1d9]'
      : 'rounded-[10px] border border-[#e5e7eb] bg-[#f3f4f6] text-[#374151]',
    locationBox: isDark
      ? 'rounded-[10px] border border-[#2d3748] bg-[#1c2432]'
      : 'rounded-[10px] border border-[#e5e7eb] bg-[#f3f4f6]',
  };
}

export function getPandoyTheme(isDark: boolean) {
  return {
    panel: isDark
      ? 'bg-[#161b22] border border-[#2d3748] text-[#e6edf3]'
      : 'bg-[#f8f5ec] border border-[#e5e7eb] text-[#111827]',
    header: isDark
      ? 'bg-[#161b22] border-[#2d3748]'
      : 'bg-white border-[#e5e7eb]',
    aside: isDark
      ? 'bg-[#161b22] border-r border-[#2d3748] text-[#e6edf3]'
      : 'bg-white border-r border-[#e5e7eb] text-[#111827]',
    inputWrap: isDark
      ? 'bg-[#1c2432] border-[#2d3748]'
      : 'bg-white border-[#e5e7eb]',
    heading: isDark ? 'text-[#e6edf3]' : 'text-[#111827]',
    muted: isDark ? 'text-[#8b949e]' : 'text-[#6b7280]',
    iconBtn: isDark
      ? 'text-[#8b949e] hover:bg-white/[0.06] hover:text-[#e6edf3]'
      : 'text-[#6b7280] hover:bg-[#f3f4f6] hover:text-[#008540]',
    messages: isDark ? 'bg-[#161b22]' : 'bg-[#f8f5ec]',
    composer: isDark
      ? 'border-[#2d3748] bg-[#161b22]'
      : 'border-[#e5e7eb] bg-white',
    userBubble: isDark
      ? 'rounded-[16px_6px_16px_16px] text-white bg-[#008540]'
      : 'rounded-[16px_6px_16px_16px] text-white bg-[#008540]',
    assistantBubble: isDark
      ? 'rounded-[6px_16px_16px_16px] border border-[#2d3748] bg-[#1c2432] text-[#e6edf3]'
      : 'rounded-[6px_16px_16px_16px] border border-[#e5e7eb] bg-white text-[#111827]',
    fieldChip: isDark
      ? 'bg-[#1c2432] text-[#c9d1d9] border border-[#2d3748]'
      : 'bg-[#f3f4f6] text-[#008540] border border-[#e5e7eb]',
    thinkingOn: isDark
      ? 'border-[#34d399]/40 text-[#34d399] bg-[rgba(52,211,153,.12)]'
      : 'border-[#00854080] text-[#008540] bg-[#00854014]',
    thinkingOff: isDark
      ? 'border-[#2d3748] text-[#8b949e] hover:bg-white/[0.06]'
      : 'border-[#e5e7eb] text-[#6b7280] hover:bg-[#f3f4f6]',
    sendOn: isDark
      ? 'bg-[#008540] text-white hover:bg-[#097a34]'
      : 'bg-[#008540] text-white hover:opacity-90',
    sendOff: isDark
      ? 'bg-[#253347] text-[#6e7681] cursor-not-allowed'
      : 'bg-[#f3f4f6] text-[#6b7280] cursor-not-allowed',
    attach: isDark
      ? 'border-[#2d3748] bg-[#1c2432] text-[#c9d1d9]'
      : 'border-[#e5e7eb] bg-[#f3f4f6] text-[#374151]',
  };
}
